import type { Session, User } from '@supabase/supabase-js';
import * as Linking from 'expo-linking';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import i18n from '@/i18n';
import { supabase } from '@/lib/supabase';
import { unregisterCurrentDevice } from '@/lib/notifications';

/** Διεύθυνση που ανοίγει η εφαρμογή στην οθόνη νέου κωδικού (deep link). */
export const RESET_PASSWORD_REDIRECT = Linking.createURL('reset-password');

type AuthLinkTokens = {
  access_token: string;
  refresh_token: string;
  type: 'recovery' | 'signup' | string;
};

/**
 * Διαβάζει tokens από το fragment (#) ενός Supabase deep link (implicit flow).
 * Καλύπτει τόσο type=recovery (επαναφορά κωδικού) όσο και type=signup (επιβεβαίωση email).
 */
function parseAuthLinkTokens(url: string): AuthLinkTokens | null {
  const hashIndex = url.indexOf('#');
  if (hashIndex === -1) return null;
  const params = new URLSearchParams(url.slice(hashIndex + 1));
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');
  const type = params.get('type');
  if (!access_token || !refresh_token || !type) return null;
  return { access_token, refresh_token, type };
}

type AuthResult = { error: string | null };
/** Επιπλέον για την εγγραφή: αν δεν δημιουργήθηκε session, χρειάζεται επιβεβαίωση email. */
type SignUpResult = AuthResult & { needsConfirmation: boolean };

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  /** true μέχρι να φορτωθεί η αρχική session από το storage. */
  initializing: boolean;
  /** true όταν ανοίξαμε από link επαναφοράς — μένουμε στην οθόνη νέου κωδικού. */
  passwordRecovery: boolean;
  signUp: (email: string, password: string) => Promise<SignUpResult>;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  /** Στέλνει email επαναφοράς (link + 6-ψήφιος κωδικός). */
  requestPasswordReset: (email: string) => Promise<AuthResult>;
  /** Επαληθεύει τον 6-ψήφιο κωδικό επαναφοράς και θέτει νέο κωδικό. */
  confirmPasswordReset: (
    email: string,
    code: string,
    newPassword: string,
  ) => Promise<AuthResult>;
  /** Θέτει νέο κωδικό όταν υπάρχει ήδη session ανάκτησης (από link). */
  setNewPassword: (newPassword: string) => Promise<AuthResult>;
  /** Καθαρίζει το recovery mode (μετά την επιτυχή αλλαγή). */
  clearPasswordRecovery: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Μεταφράζει συχνά σφάλματα του Supabase σε φιλικά μηνύματα, στη γλώσσα της εφαρμογής. */
function toGreekError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('invalid login credentials')) return i18n.t('auth.error_invalidCredentials');
  if (m.includes('user already registered')) return i18n.t('auth.error_alreadyRegistered');
  if (m.includes('password should be at least')) return i18n.t('auth.error_passwordTooShort');
  if (m.includes('unable to validate email')) return i18n.t('auth.error_invalidEmail');
  if (m.includes('email not confirmed')) return i18n.t('auth.error_emailNotConfirmed');
  if (m.includes('token has expired') || m.includes('expired'))
    return i18n.t('auth.error_codeExpired');
  if (m.includes('invalid') && m.includes('token')) return i18n.t('auth.error_invalidResetCode');
  if (m.includes('otp') || m.includes('invalid token')) return i18n.t('auth.error_invalidOrExpiredCode');
  if (m.includes('same') && m.includes('password'))
    return i18n.t('auth.error_samePassword');
  if (m.includes('rate limit') || m.includes('too many'))
    return i18n.t('auth.error_rateLimit');
  return message;
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setInitializing(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      // Στο web (detectSessionInUrl=false) το event μπορεί να μην έρθει· καλύπτεται
      // και από τον deep-link handler πιο κάτω.
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  // Deep-link handler για Supabase auth links (implicit flow, fragment-based tokens):
  //   type=recovery → επαναφορά κωδικού (heirapsia://reset-password#...)
  //   type=signup   → επιβεβαίωση email (heirapsia://auth/callback#...)
  // Και στις δύο περιπτώσεις κάνουμε setSession· για recovery επιπλέον
  // μπαίνουμε σε recovery mode ώστε ο guard να οδηγήσει στην οθόνη νέου κωδικού.
  useEffect(() => {
    let active = true;

    async function handleUrl(url: string | null) {
      if (!url || !active) return;
      const tokens = parseAuthLinkTokens(url);
      if (!tokens) return;
      const { error } = await supabase.auth.setSession({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
      });
      if (error || !active) return;
      if (tokens.type === 'recovery') setPasswordRecovery(true);
      // type=signup: το setSession() δημιουργεί session → ο guard στο _layout.tsx
      // ανιχνεύει session && inAuthGroup και ανακατευθύνει αυτόματα στο /(app).
    }

    void Linking.getInitialURL().then(handleUrl);
    const sub = Linking.addEventListener('url', ({ url }) => void handleUrl(url));

    return () => {
      active = false;
      sub.remove();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      initializing,
      passwordRecovery,
      async signUp(email, password) {
        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
        });
        // Αν δεν επιστράφηκε session, το Supabase απαιτεί επιβεβαίωση email.
        // Αν υπάρχει session, ο χρήστης μπήκε ήδη (το guard κάνει redirect).
        return {
          error: error ? toGreekError(error.message) : null,
          needsConfirmation: !data.session,
        };
      },
      async signIn(email, password) {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        return { error: error ? toGreekError(error.message) : null };
      },
      async signOut() {
        // Αφαίρεσε το push token όσο ισχύει ακόμη η session.
        await unregisterCurrentDevice();
        await supabase.auth.signOut();
      },
      async requestPasswordReset(email) {
        const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: RESET_PASSWORD_REDIRECT,
        });
        return { error: error ? toGreekError(error.message) : null };
      },
      async confirmPasswordReset(email, code, newPassword) {
        // 1) Επαλήθευση 6-ψήφιου κωδικού → προσωρινή session ανάκτησης.
        const { error: otpError } = await supabase.auth.verifyOtp({
          email: email.trim(),
          token: code.trim(),
          type: 'recovery',
        });
        if (otpError) return { error: toGreekError(otpError.message) };
        // 2) Ορισμός νέου κωδικού στη session που μόλις δημιουργήθηκε.
        const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
        return { error: updateError ? toGreekError(updateError.message) : null };
      },
      async setNewPassword(newPassword) {
        const { error } = await supabase.auth.updateUser({ password: newPassword });
        return { error: error ? toGreekError(error.message) : null };
      },
      clearPasswordRecovery() {
        setPasswordRecovery(false);
      },
    }),
    [session, initializing, passwordRecovery],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('Το useAuth πρέπει να χρησιμοποιείται μέσα σε <AuthProvider>.');
  }
  return ctx;
}
